<template>
    <div :class="wrapperClass">
        <label v-if="label" class="form-label-alt">
            <span>{{ label }}</span>
            <FieldTooltip :content="tooltipHtml"/>
        </label>
        <div class="lfmpub-select" :class="{ 'lfmpub-select--open': isOpen, 'lfmpub-select--readonly': readonly }" ref="selectRoot">
            <!-- Trigger / display -->
            <div
                v-if="!readonly"
                ref="triggerEl"
                class="form-control lfmpub-select__trigger"
                :class="{ 'is-invalid': isInvalid }"
                @click="toggle"
                tabindex="0"
                @keydown.enter.prevent="toggle"
                @keydown.escape="close"
            >
                <span v-if="displayLabel" class="lfmpub-select__label">{{ displayLabel }}</span>
                <span v-else class="lfmpub-select__placeholder">{{ placeholder }}</span>
                <span v-if="modelValue != null && modelValue !== ''" class="lfmpub-select__clear" @click.stop="clear">&times;</span>
                <span class="lfmpub-select__arrow ms-1"><i :class="isOpen ? 'fa-thin fa-angle-up' : 'fa-thin fa-angle-down'"></i></span>
            </div>
            <!-- Readonly display: static label, no dropdown -->
            <div v-else class="form-control form-control-readonly lfmpub-select__trigger lfmpub-select__trigger--readonly">
                <span v-if="displayLabel" class="lfmpub-select__label">{{ displayLabel }}</span>
                <span v-else class="lfmpub-select__placeholder">&nbsp;</span>
            </div>

            <!-- Hidden input for Parsley validation (mirrors select value) -->
            <input
                type="text"
                class="d-none"
                tabindex="-1"
                aria-hidden="true"
                :value="modelValue != null && modelValue !== '' ? String(modelValue) : ''"
                :required="!readonly && required || undefined"
                :data-parsley-recommended="!readonly && recommended ? 'true' : undefined"
                :data-parsley-validate-if-empty="!readonly && recommended ? 'true' : undefined"
                :data-parsley-no-focus="!readonly && recommended ? 'true' : undefined"
            >

            <!-- Dropdown teleported to body to escape clip-path / overflow -->
            <Teleport to="body">
                <div
                    v-if="isOpen"
                    ref="dropdownEl"
                    class="lfmpub-select__dropdown"
                    :style="dropdownStyle"
                >
                    <input
                        ref="searchInput"
                        v-model="search"
                        class="form-control lfmpub-select__search"
                        placeholder="Suchen..."
                        @input="onSearchInput"
                        @keydown.escape="close"
                        @keydown.enter.prevent="selectFirst"
                    >
                    <div class="lfmpub-select__options" ref="optionsEl">
                        <slot name="before-options" />
                        <div
                            v-for="opt in filteredOptions"
                            :key="optVal(opt)"
                            class="lfmpub-select__option"
                            :class="{
                                'lfmpub-select__option--active': isSelected(opt),
                                'lfmpub-select__option--disabled': isDisabled(opt),
                            }"
                            @click="select(opt)"
                        >
                            {{ optLabel(opt) }}
                        </div>

                        <!-- AJAX "loading more" indicator -->
                        <div v-if="ajaxLoading" class="lfmpub-select__option lfmpub-select__option--loading">
                            Laden...
                        </div>

                        <!-- Create new option -->
                        <div
                            v-if="canCreate && search.trim() && !hasExactMatch"
                            class="lfmpub-select__option lfmpub-select__option--create"
                            @click="createNew"
                        >
                            Erstellen: "{{ search.trim() }}"
                        </div>

                        <div v-if="filteredOptions.length === 0 && !ajaxLoading && !(canCreate && search.trim())"
                             class="lfmpub-select__option lfmpub-select__option--empty">
                            Keine Ergebnisse
                        </div>
                    </div>
                </div>
            </Teleport>
        </div>
        <div v-if="hasError" class="form-error">
            <div v-if="required">Dieses Feld ist ein Pflichtfeld.</div>
            <div v-else-if="recommended">Dieses Feld wird empfohlen auszufüllen</div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, watch, nextTick, inject, onBeforeUnmount, toRef} from 'vue';
import FieldTooltip from './FieldTooltip.vue';
import {useFieldTooltip} from '../useFieldTooltip';

const props = defineProps({
    /** UID (number) for existing entity, or string for "create new" */
    modelValue: { type: [Number, String], default: null },
    options: { type: Array, default: () => [] },
    optionValue: { type: String, default: 'uid' },
    optionLabel: { type: String, default: 'name' },
    label: { type: String, default: '' },
    /** Field description key, @see FieldDescriptionService::GROUPS */
    tooltip: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    required: { type: Boolean, default: false },
    recommended: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    highlight: { type: Boolean, default: false },
    noMargin: { type: Boolean, default: false },
    canCreate: { type: Boolean, default: false },
    /** URL for AJAX option loading (e.g. keyword search) */
    ajaxUrl: { type: String, default: '' },
    /** Option values to render as disabled (cannot be selected). Currently selected value is never disabled. */
    disabledValues: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const submitted = inject('submitted', { value: false });
const tooltipHtml = useFieldTooltip(toRef(props, 'tooltip'));

const isOpen = ref(false);
const search = ref('');
const triggerEl = ref(null);
const dropdownEl = ref(null);
const searchInput = ref(null);
const selectRoot = ref(null);
const ajaxLoading = ref(false);
const ajaxOptions = ref([]);
let ajaxAbort = null;

// --- Dropdown positioning ---

const dropdownStyle = ref({});

function updateDropdownPosition() {
    if (!triggerEl.value) return;
    const rect = triggerEl.value.getBoundingClientRect();
    dropdownStyle.value = {
        position: 'fixed',
        top: `${rect.bottom}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: 1050,
    };
}

function onScrollOrResize() {
    if (isOpen.value) updateDropdownPosition();
}

function onDocumentClick(e) {
    if (!isOpen.value) return;
    // Close if click is outside both trigger and dropdown
    if (
        triggerEl.value && !triggerEl.value.contains(e.target) &&
        dropdownEl.value && !dropdownEl.value.contains(e.target)
    ) {
        close();
    }
}

watch(isOpen, (val) => {
    if (val) {
        updateDropdownPosition();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        document.addEventListener('mousedown', onDocumentClick, true);
    } else {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
        document.removeEventListener('mousedown', onDocumentClick, true);
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
    document.removeEventListener('mousedown', onDocumentClick, true);
});

// --- Validation ---

const selectIsEmpty = computed(() => props.modelValue == null || props.modelValue === '');

/** Red outline — always visible when required + empty */
const isInvalid = computed(() => !props.readonly && props.required && selectIsEmpty.value);

/** Recommended warning — shown after first submit attempt */
const isRecommendedEmpty = computed(() => !props.readonly && props.recommended && selectIsEmpty.value);

/** Error/warning message — only after first submit attempt */
const hasError = computed(() => submitted.value && (isInvalid.value || isRecommendedEmpty.value));

// --- Options ---

// Merge static + AJAX options, deduplicate by value key
const allOptions = computed(() => {
    if (!props.ajaxUrl) return props.options;
    const staticMap = new Map(props.options.map(o => [o[props.optionValue], o]));
    for (const o of ajaxOptions.value) {
        if (!staticMap.has(o[props.optionValue])) {
            staticMap.set(o[props.optionValue], o);
        }
    }
    return Array.from(staticMap.values());
});

const filteredOptions = computed(() => {
    const term = search.value.toLowerCase().trim();
    if (!term) return allOptions.value;
    return allOptions.value.filter(opt =>
        String(opt[props.optionLabel] ?? '').toLowerCase().includes(term)
    );
});

const hasExactMatch = computed(() => {
    const term = search.value.toLowerCase().trim();
    if (!term) return false;
    return allOptions.value.some(opt =>
        String(opt[props.optionLabel] ?? '').toLowerCase() === term
    );
});

const displayLabel = computed(() => {
    if (props.modelValue == null || props.modelValue === '') return '';
    // If it's a created string value
    if (typeof props.modelValue === 'string') return props.modelValue;
    // Find in options
    const opt = allOptions.value.find(o => o[props.optionValue] == props.modelValue);
    return opt ? opt[props.optionLabel] : '';
});

const wrapperClass = computed(() => ({
    'form-highlight': props.required || props.recommended || props.highlight,
    'mb-3': !props.noMargin,
}));

function optVal(opt) { return opt[props.optionValue]; }
function optLabel(opt) { return opt[props.optionLabel] ?? ''; }

function isSelected(opt) {
    return opt[props.optionValue] == props.modelValue;
}

function isDisabled(opt) {
    if (!props.disabledValues.length) return false;
    // Never disable the currently selected value (so the row keeps showing its own role)
    if (isSelected(opt)) return false;
    const v = opt[props.optionValue];
    // Loose equality to match number/string variants (UIDs may arrive as either)
    return props.disabledValues.some(d => d == v);
}

// --- Open / Close ---

function toggle() {
    isOpen.value ? close() : open();
}

function open() {
    isOpen.value = true;
    search.value = '';
    nextTick(() => {
        updateDropdownPosition();
        searchInput.value?.focus();
    });
    if (props.ajaxUrl) {
        fetchAjax('');
    }
}

function close() {
    isOpen.value = false;
    search.value = '';
}

function select(opt) {
    if (isDisabled(opt)) return;
    emit('update:modelValue', opt[props.optionValue]);
    close();
}

function selectFirst() {
    const firstSelectable = filteredOptions.value.find(o => !isDisabled(o));
    if (firstSelectable) {
        select(firstSelectable);
    } else if (props.canCreate && search.value.trim() && !hasExactMatch.value) {
        createNew();
    }
}

function clear() {
    emit('update:modelValue', null);
}

function createNew() {
    emit('update:modelValue', search.value.trim());
    close();
}

// --- AJAX ---

let debounceTimer = null;
function onSearchInput() {
    if (!props.ajaxUrl) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => fetchAjax(search.value), 250);
}

async function fetchAjax(term) {
    if (!props.ajaxUrl) return;
    if (ajaxAbort) ajaxAbort.abort();
    ajaxAbort = new AbortController();
    ajaxLoading.value = true;
    try {
        const url = new URL(props.ajaxUrl, window.location.origin);
        url.searchParams.set('term', term);
        url.searchParams.set('page', '1');
        const resp = await fetch(url.toString(), { signal: ajaxAbort.signal });
        const data = await resp.json();
        // API returns { results: [{id, text}], pagination: {more} }
        ajaxOptions.value = (data.results || []).map(r => ({
            [props.optionValue]: r.id,
            [props.optionLabel]: r.text,
        }));
    } catch (e) {
        if (e.name !== 'AbortError') console.error('AJAX error:', e);
    } finally {
        ajaxLoading.value = false;
    }
}
</script>
